import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, MessageCircle, FileSignature } from 'lucide-react';
import { CONTACT, PROGRAM_OPTIONS, WAIVER_TEXT } from '@/data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = 'w-full bg-black/50 border border-white/20 rounded-sm px-4 py-3 text-white placeholder:text-gray-600 focus:border-brand-gold focus:outline-none focus:ring-1 focus:ring-brand-gold/60 transition-[border-color,box-shadow] duration-300 text-sm';
const labelCls = 'block text-xs uppercase tracking-[0.2em] text-gray-400 font-semibold mb-2';

const INITIAL = {
  parentName: '', playerName: '', dob: '', age: '', gender: '', phone: '', email: '',
  currentTeam: '', position: '', experienceLevel: '', program: '', trainingDays: '', message: '',
  waiverAgreed: false, waiverSignature: '', consent: false,
};

const matchProgram = (p) => {
  if (!p) return '';
  return PROGRAM_OPTIONS.find(o => o === p || o.startsWith(p) || o.toLowerCase().includes(p.toLowerCase())) || p;
};

export default function RegistrationForm({ initialProgram = '', formType = 'registration', heading = 'PLAYER REGISTRATION' }) {
  const [form, setForm] = useState({ ...INITIAL, program: matchProgram(initialProgram) });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waLink, setWaLink] = useState('');

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [key]: value }));
  };

  const buildWhatsAppLink = (data) => {
    const lines = [
      'NEW ASA REGISTRATION',
      `Type: ${formType}`,
      `Player: ${data.playerName}`,
      `Parent/Guardian: ${data.parentName}`,
      `DOB: ${data.dob || '-'} | Age: ${data.age || '-'} | Gender: ${data.gender || '-'}`,
      `Phone: ${data.phone}`,
      `Email: ${data.email}`,
      `Current Team: ${data.currentTeam || '-'}`,
      `Position: ${data.position || '-'}`,
      `Experience: ${data.experienceLevel || '-'}`,
      `Program: ${data.program || '-'}`,
      `Training Days: ${data.trainingDays || '-'}`,
      data.message ? `Message: ${data.message}` : null,
      `Waiver signed by: ${data.waiverSignature}`,
    ].filter(Boolean);
    return `${CONTACT.whatsapp.split('?')[0]}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.waiverAgreed || !form.waiverSignature.trim()) {
      toast.error('Please read and sign the liability waiver before submitting.');
      return;
    }
    if (!form.consent) {
      toast.error('Please agree to be contacted before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, { ...form, formType });
      const link = buildWhatsAppLink(form);
      setWaLink(link);
      setSubmitted(true);
      toast.success('Registration received. ASA will contact you shortly.');
      window.open(link, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try WhatsApp instead.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass-card gold-hairline rounded-sm p-10 sm:p-14 text-center" data-testid="registration-confirmation">
        <CheckCircle2 size={56} className="mx-auto text-brand-gold" />
        <h3 className="mt-6 font-display text-3xl sm:text-4xl text-white uppercase tracking-wide">Registration Received</h3>
        <p className="mt-4 text-gray-300 max-w-md mx-auto leading-relaxed">
          Thank you, {form.parentName.split(' ')[0] || 'Champion'}. One last step — send your registration to our WhatsApp so the coaching team receives every detail instantly.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={waLink || CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="confirmation-whatsapp-button"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-night font-display tracking-widest text-lg px-8 py-3.5 rounded-sm hover:brightness-110 transition-[filter] duration-300">
            <MessageCircle size={18} /> COMPLETE ON WHATSAPP — أكمل التسجيل
          </a>
          <button onClick={() => { setSubmitted(false); setForm({ ...INITIAL, program: matchProgram(initialProgram) }); }} data-testid="confirmation-new-registration-button"
            className="border border-white/20 text-gray-300 hover:border-brand-gold hover:text-brand-gold font-display tracking-widest text-lg px-8 py-3.5 rounded-sm transition-colors duration-300">
            REGISTER ANOTHER PLAYER
          </button>
        </div>
        <p className="mt-4 text-xs text-gray-500">Your details were saved — the WhatsApp message sends everything straight to the ASA team at {CONTACT.phoneDisplay}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass-card rounded-sm p-7 sm:p-10" data-testid="registration-form">
      <h3 className="font-display text-2xl sm:text-3xl text-white uppercase tracking-wide mb-8">{heading}</h3>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="reg-parent-name">Parent / Guardian Name *</label>
          <input id="reg-parent-name" data-testid="reg-parent-name" required className={inputCls} value={form.parentName} onChange={set('parentName')} placeholder="Full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-player-name">Player Name *</label>
          <input id="reg-player-name" data-testid="reg-player-name" required className={inputCls} value={form.playerName} onChange={set('playerName')} placeholder="Player's full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-dob">Date of Birth</label>
          <input id="reg-dob" data-testid="reg-dob" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.dob} onChange={set('dob')} />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-age">Age</label>
          <input id="reg-age" data-testid="reg-age" type="number" min="4" max="17" className={inputCls} value={form.age} onChange={set('age')} placeholder="5–16" />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-gender">Gender</label>
          <select id="reg-gender" data-testid="reg-gender" className={inputCls} value={form.gender} onChange={set('gender')}>
            <option value="">Select…</option>
            <option>Boy</option>
            <option>Girl</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-phone">Phone *</label>
          <input id="reg-phone" data-testid="reg-phone" required type="tel" className={inputCls} value={form.phone} onChange={set('phone')} placeholder="(734) 555-0000" />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-email">Email *</label>
          <input id="reg-email" data-testid="reg-email" required type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@email.com" />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-team">Current Team</label>
          <input id="reg-team" data-testid="reg-team" className={inputCls} value={form.currentTeam} onChange={set('currentTeam')} placeholder="e.g. Canton Celtic U10 (or 'None')" />
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-position">Playing Position</label>
          <select id="reg-position" data-testid="reg-position" className={inputCls} value={form.position} onChange={set('position')}>
            <option value="">Select…</option>
            <option>Goalkeeper</option>
            <option>Defender</option>
            <option>Midfielder</option>
            <option>Forward</option>
            <option>Not sure yet</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-experience">Experience Level</label>
          <select id="reg-experience" data-testid="reg-experience" className={inputCls} value={form.experienceLevel} onChange={set('experienceLevel')}>
            <option value="">Select…</option>
            <option>Beginner (0–1 years)</option>
            <option>Recreational (1–3 years)</option>
            <option>Travel / Club (3+ years)</option>
            <option>Advanced / Elite</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-program">Preferred Program</label>
          <select id="reg-program" data-testid="reg-program" className={inputCls} value={form.program} onChange={set('program')}>
            <option value="">Select…</option>
            {PROGRAM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="reg-days">Preferred Training Days</label>
          <select id="reg-days" data-testid="reg-days" className={inputCls} value={form.trainingDays} onChange={set('trainingDays')}>
            <option value="">Select…</option>
            <option>Weekday evenings</option>
            <option>Weekends</option>
            <option>Flexible / either</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="reg-message">Message</label>
          <textarea id="reg-message" data-testid="reg-message" rows={4} className={inputCls} value={form.message} onChange={set('message')} placeholder="Anything you'd like the coaches to know about your player…" />
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls}>
            <span className="inline-flex items-center gap-2"><FileSignature size={13} className="text-brand-gold" /> Liability Waiver — Required by the State of Michigan *</span>
          </label>
          <div className="max-h-44 overflow-y-auto bg-black/60 border border-white/15 rounded-sm p-4 text-xs text-gray-400 leading-relaxed" data-testid="reg-waiver-text">
            {WAIVER_TEXT}
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="reg-waiver-label">
              <input type="checkbox" data-testid="reg-waiver" checked={form.waiverAgreed} onChange={set('waiverAgreed')}
                className="mt-1 h-4 w-4 rounded-sm border-white/30 bg-black/50 accent-[#C5A059]" />
              <span className="text-sm text-gray-400 leading-relaxed">
                I have read and agree to the Abu Zama Soccer Academy liability waiver.
              </span>
            </label>
            <div>
              <label className={labelCls} htmlFor="reg-waiver-signature">Signature — Type Full Legal Name *</label>
              <input id="reg-waiver-signature" data-testid="reg-waiver-signature" className={inputCls}
                value={form.waiverSignature} onChange={set('waiverSignature')} placeholder="Parent / guardian full name" />
            </div>
          </div>
        </div>
      </div>

      <label className="mt-7 flex items-start gap-3 cursor-pointer" data-testid="reg-consent-label">
        <input type="checkbox" data-testid="reg-consent" checked={form.consent} onChange={set('consent')}
          className="mt-1 h-4 w-4 rounded-sm border-white/30 bg-black/50 accent-[#C5A059]" />
        <span className="text-sm text-gray-400 leading-relaxed">
          I agree to be contacted by Abu Zama Soccer Academy regarding registration and training opportunities.
        </span>
      </label>

      <button type="submit" disabled={submitting} data-testid="registration-submit-button"
        className="mt-8 w-full bg-brand-red hover:bg-brand-redhover disabled:opacity-60 text-white font-display tracking-widest text-2xl py-4 rounded-sm transition-colors duration-300 flex items-center justify-center gap-3">
        {submitting && <Loader2 size={20} className="animate-spin" />}
        SUBMIT REGISTRATION
      </button>
    </form>
  );
}
